/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { AlertCircle } from "lucide-react";
import { type FC, type ReactNode } from "react";
import { Button } from "./button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./card";
import { theme } from "../../styles";

export interface ErrorStateProps {
  error: Error | { message?: string } | string;
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: ReactNode;
  className?: string;
}

/**
 * Generic error state component for full-page error states.
 * Displays an error message with optional action button.
 */
export const ErrorState: FC<ErrorStateProps> = ({
  error,
  title = "Something went wrong",
  description,
  actionLabel = "Refresh Page",
  onAction,
  icon,
  className,
}) => {
  const errorMessage =
    typeof error === "string" ? error : error?.message || "An unexpected error occurred.";

  const handleAction = onAction || (() => window.location.reload());

  return (
    <div
      className={className}
      css={css`
        width: 100%;
        min-height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2rem;
      `}
    >
      <Card
        css={css`
          width: 100%;
          max-width: 42rem;
        `}
      >
        <CardHeader>
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 0.75rem;
            `}
          >
            {icon || (
              <AlertCircle
                css={css`
                  width: 1.5rem;
                  height: 1.5rem;
                  color: ${theme.colors.status.error};
                `}
              />
            )}
            <CardTitle>{title}</CardTitle>
          </div>
          <CardDescription>{description || errorMessage}</CardDescription>
        </CardHeader>
        {actionLabel && (
          <CardContent>
            <Button onClick={handleAction} variant="default">
              {actionLabel}
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
};
